import {
  Activity,
  CheckCircle2,
  Clock,
  MessageSquare,
  Pause,
  Play,
  SkipForward,
  Square,
} from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type { TaskActivity } from '@/services/api/generated/taskActivityService';

import { EventBody, EventContainer, EventHeader } from './EventPrimitives';

interface LOAChaserEventProps {
  activityItem: TaskActivity;
}

// LOA Chaser event payload types (until API types are generated)
interface LOAChaserPayload {
  campaign?: {
    current_step_label?: string;
  };
  // Step executed event fields
  step_label?: string;
  step_type?: string;
  was_auto?: boolean;
  // Step changed event fields
  new_step_label?: string;
  direction?: string;
  // Manual activity logged fields
  activity_type?: string;
  notes?: string;
  executed_at?: string;
  // Campaign stopped fields
  reason?: string;
  // Campaign completed fields
  outcome?: string;
  // Snooze fields
  snooze_days?: number;
}

// Map event codes to display info
const EVENT_CONFIG: Record<
  string,
  {
    title: string;
    icon: React.ReactNode;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
  }
> = {
  loa_chaser_campaign_started: {
    title: 'LOA Chaser Started',
    icon: <Activity size={20} />,
    variant: 'default',
  },
  loa_chaser_campaign_paused: {
    title: 'LOA Chaser Paused',
    icon: <Pause size={20} />,
    variant: 'secondary',
  },
  loa_chaser_campaign_resumed: {
    title: 'LOA Chaser Resumed',
    icon: <Play size={20} />,
    variant: 'default',
  },
  loa_chaser_campaign_stopped: {
    title: 'LOA Chaser Stopped',
    icon: <Square size={20} />,
    variant: 'destructive',
  },
  loa_chaser_campaign_completed: {
    title: 'LOA Chaser Completed',
    icon: <CheckCircle2 size={20} />,
    variant: 'default',
  },
  loa_chaser_step_executed: {
    title: 'LOA Chaser Step Executed',
    icon: <Activity size={20} />,
    variant: 'secondary',
  },
  loa_chaser_step_changed: {
    title: 'LOA Chaser Step Changed',
    icon: <SkipForward size={20} />,
    variant: 'secondary',
  },
  loa_chaser_manual_activity_logged: {
    title: 'LOA Chaser Activity Logged',
    icon: <MessageSquare size={20} />,
    variant: 'secondary',
  },
  loa_chaser_schedule_realigned: {
    title: 'LOA Chaser Schedule Updated',
    icon: <Clock size={20} />,
    variant: 'outline',
  },
  loa_chaser_snoozed: {
    title: 'LOA Chaser Snoozed',
    icon: <Clock size={20} />,
    variant: 'outline',
  },
};

export const LOAChaserEvent: React.FC<LOAChaserEventProps> = ({
  activityItem,
}) => {
  const { event } = activityItem;
  const { event_code, occurred_at, user, agent } = event;

  // Cast payload to LOAChaserPayload (types not yet generated in API)
  const payload = event.payload as unknown as LOAChaserPayload;

  const config = EVENT_CONFIG[event_code] || {
    title: formatEventCode(event_code),
    icon: <Activity size={20} />,
    variant: 'outline' as const,
  };

  // Extract useful info from payload
  const campaign = payload?.campaign;
  const reason = payload?.reason;
  const outcome = payload?.outcome;
  const activityType = payload?.activity_type;
  const notes = payload?.notes;
  const snoozeDays = payload?.snooze_days;
  const direction = payload?.direction;
  const newStepLabel = payload?.new_step_label;
  // Step executed fields
  const stepLabel = payload?.step_label;
  const stepType = payload?.step_type;
  const wasAuto = payload?.was_auto;

  return (
    <EventContainer>
      <EventHeader
        icon={config.icon}
        title={config.title}
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Current step info (for campaign lifecycle events) */}
          {campaign?.current_step_label && !stepLabel && (
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className="text-slate-600">Step:</span>
              <Badge variant="outline">{campaign.current_step_label}</Badge>
            </div>
          )}

          {/* Step executed info */}
          {stepLabel && (
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className="text-slate-600">
                {wasAuto === false ? 'Manual' : 'Auto'}:
              </span>
              {stepType && (
                <Badge variant="outline">{formatActivityType(stepType)}</Badge>
              )}
              <span className="text-slate-500">— {stepLabel}</span>
            </div>
          )}

          {/* Step change info */}
          {direction && newStepLabel && (
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className="text-slate-600">
                {direction === 'forward' ? 'Skipped to:' : 'Reset to:'}
              </span>
              <Badge variant={config.variant}>{newStepLabel}</Badge>
            </div>
          )}

          {/* Activity type for manual activity */}
          {activityType && (
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className="text-slate-600">Activity:</span>
              <Badge variant="outline">
                {formatActivityType(activityType)}
              </Badge>
            </div>
          )}

          {/* Notes */}
          {notes && (
            <div className="text-xs text-slate-600 mt-2">
              <span className="font-medium">Notes:</span> {notes}
            </div>
          )}

          {/* Reason for stop */}
          {reason && (
            <div className="text-xs text-slate-600 mt-2">
              <span className="font-medium">Reason:</span> {reason}
            </div>
          )}

          {/* Outcome for completion */}
          {outcome && (
            <div className="flex items-center gap-2 text-xs mt-2">
              <span className="text-slate-600">Outcome:</span>
              <Badge variant={config.variant}>{formatOutcome(outcome)}</Badge>
            </div>
          )}

          {/* Snooze days */}
          {snoozeDays && (
            <div className="text-xs text-slate-600 mt-2">
              Snoozed for {snoozeDays} day{snoozeDays > 1 ? 's' : ''}
            </div>
          )}
        </div>
      </EventBody>
    </EventContainer>
  );
};

function formatEventCode(code: string): string {
  return code
    .replace(/^loa_chaser_/, '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatActivityType(activityType: string): string {
  const labels: Record<string, string> = {
    manual_call: 'Phone Call',
    manual_email: 'Email Sent',
    supervisor_escalation: 'Supervisor Escalation',
    auto_email: 'Email',
    auto_fax: 'Fax',
  };
  return labels[activityType] || activityType.replace(/_/g, ' ');
}

function formatOutcome(outcome: string): string {
  const labels: Record<string, string> = {
    completed_loa_received: 'LOA Received',
    completed_no_response: 'No Response',
    stopped_manually: 'Stopped',
  };
  return labels[outcome] || outcome.replace(/_/g, ' ');
}
